import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, retry } from 'rxjs';
import { Employee } from '../models/employee.model';
import { environment } from '../../environments/environment';

export interface CreateEmployeeDto {
  name: string;
  email?: string;
  roleId: string;
  roleIds?: string[];
  designationId?: string;
  categoryId?: string;
  serviceGroupId: string;
  serviceGroupIds?: string[];
  isActive: boolean;
  inactiveDate?: string;
  excludeFromAllocation: boolean;
  excludeFromForecast: boolean;
  excludeFromTimesheet: boolean;
  /** Keeps someone off the 12-month Future Allocation planner. */
  excludeFromFutureAllocation: boolean;
}

/** One related table's row count for an employee. */
export interface EmployeeReferenceCount {
  label: string;
  count: number;
  /** True when this is business history — losing it would destroy a record of real work. */
  isHistorical: boolean;
}

/** Everything referencing an employee, used to decide whether a permanent delete is safe. */
export interface EmployeeReferences {
  employeeId: string;
  employeeName: string;
  isActive: boolean;
  references: EmployeeReferenceCount[];
  canDeleteSafely: boolean;
  totalReferences: number;
}

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {
  private apiUrl = `${environment.apiUrl}/Employee`;

  constructor(private http: HttpClient) { }

  getAllEmployees(): Observable<Employee[]> {
    return this.http.get<Employee[]>(this.apiUrl).pipe(retry(2));
  }

  getEmployeeNames(): Observable<{ id: string; name: string }[]> {
    return this.http.get<{ id: string; name: string }[]>(`${this.apiUrl}/names`);
  }

  getEmployee(id: string): Observable<Employee> {
    return this.http.get<Employee>(`${this.apiUrl}/${id}`).pipe(retry(2));
  }

  addEmployee(employee: Employee): Observable<Employee> {
    const roleIds = (employee.roleIds && employee.roleIds.length > 0) ? employee.roleIds : [employee.roleId];
    const serviceGroupIds = (employee.serviceGroupIds && employee.serviceGroupIds.length > 0) ? employee.serviceGroupIds : (employee.serviceGroupId ? [employee.serviceGroupId] : []);
    const createDto = {
      name: employee.name,
      email: employee.email,
      roleId: roleIds[0],
      roleIds: roleIds,
      designationId: employee.designationId,
      categoryId: employee.categoryId,
      serviceGroupId: serviceGroupIds[0] ?? '',
      serviceGroupIds: serviceGroupIds.length > 0 ? serviceGroupIds : undefined,
      isActive: employee.isActive,
      inactiveDate: employee.inactiveDate,
      excludeFromAllocation: employee.excludeFromAllocation ?? false,
      excludeFromForecast: employee.excludeFromForecast ?? false,
      excludeFromTimesheet: employee.excludeFromTimesheet ?? false,
      excludeFromFutureAllocation: employee.excludeFromFutureAllocation ?? false,
      // Personal / onboarding fields
      firstName: employee.firstName,
      entraIdGroup: employee.entraIdGroup,
      idNumber: employee.idNumber,
      mobileNumber: employee.mobileNumber,
      residentialAddress: employee.residentialAddress,
      currentAddress: employee.currentAddress,
      personalEmail: employee.personalEmail,
      linkedInUrl: employee.linkedInUrl,
      employmentType: employee.employmentType,
      headOfDepartmentId: employee.headOfDepartmentId,
      joiningDate: employee.joiningDate,
      emergencyContactName: employee.emergencyContactName,
      emergencyContactNumber: employee.emergencyContactNumber,
      cvAttachmentUrl: employee.cvAttachmentUrl
    };
    return this.http.post<Employee>(this.apiUrl, createDto);
  }

  updateEmployee(id: string, employee: Employee): Observable<Employee> {
    return this.http.put<Employee>(`${this.apiUrl}/${id}`, employee);
  }

  getEmployeeRoleIds(employeeId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/${employeeId}/roles`).pipe(retry(2));
  }

  /** What references this employee — shown in the delete confirmation so the user can judge. */
  getEmployeeReferences(employeeId: string): Observable<EmployeeReferences> {
    return this.http.get<EmployeeReferences>(`${this.apiUrl}/${employeeId}/references`);
  }

  /** Permanently deletes an inactive employee. Refused by the API if history would be lost. */
  deleteEmployee(employeeId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${employeeId}`);
  }

  uploadCv(file: File): Observable<{ url: string; fileName: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ url: string; fileName: string }>(`${this.apiUrl}/upload-cv`, formData);
  }
}


